import { useContext, useEffect, useState } from 'react';

import { PermissionsContext, authReducer, session } from '@bitstopco/bitstop-theme';

import { SIDEBAR_ITEMS, SIDEBAR_ITEMS_SETTINGS } from '@/constants/app/sidebar';
import { isAuth0Token } from '@/helpers/auth0Session';

import { useAppSelector } from '@/store';
import type { SidebarItemDataExtended } from '@/types';

type SidebarSubItem = NonNullable<SidebarItemDataExtended['items']>[number];

const {
  selectors: { userInfo },
} = authReducer;

const useSidebarPermissions = (settings?: boolean) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarItems, setSidebarItems] = useState<SidebarItemDataExtended[]>([]);

  const { isAllowedTo } = useContext(PermissionsContext);
  const userData = useAppSelector(userInfo);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      /**
       * If we are showing a settings view, we want to change the sidebar buttons
       */
      const sidebarItems = !settings ? SIDEBAR_ITEMS : SIDEBAR_ITEMS_SETTINGS;

      const isAuth0Session = isAuth0Token(session.getValue());

      const filteredSidebarItems: SidebarItemDataExtended[] = [];
      for (const { auth0Href, ...sidebarItem } of sidebarItems) {
        const item =
          isAuth0Session && auth0Href ? { ...sidebarItem, href: auth0Href } : sidebarItem;

        let isAllowed = true;

        if (item.permission) {
          isAllowed = await isAllowedTo(item.permission, 'AND');
        }

        if (isAllowed && item.operatorId !== undefined) {
          isAllowed = userData?.context?.operator_id === item.operatorId;
        }

        if (isAllowed) {
          if (item.items) {
            const filteredSubItems: SidebarSubItem[] = [];
            for (const subItem of item.items) {
              let isSubAllowed = true;
              if (subItem.permission) {
                isSubAllowed = await isAllowedTo(subItem.permission, 'AND');
              }
              if (isSubAllowed) {
                filteredSubItems.push(subItem);
              }
            }
            filteredSidebarItems.push({ ...item, items: filteredSubItems });
          } else {
            filteredSidebarItems.push(item);
          }
        }
      }

      setSidebarItems(filteredSidebarItems);

      setIsLoading(false);
    })();
  }, [settings, userData]);

  return { isLoading, sidebarItems };
};

export default useSidebarPermissions;
